import {Module, createStore} from "vuex";
import {MarkerFeature} from "../../../utils/models/MarkerFeature";
import store, {RootState} from "../store";
import mapboxgl from "mapbox-gl";
import {LineStringFeature} from "../../../utils/models/LineStringFeature";
import {Route, RouteFactory} from "../../../utils/models/Route";
import {StageDestination} from "../../services/stage-destinations";
import {LockedBoundary} from "../../utils/masterTrail";
import {UndoManager} from "../undo/UndoManager";
import getters from "./getters";
import mutations from "./mutations";
import actions from "./actions/index";

export class PlanTrailState {
  title: string | null = null
  description: string | null = null
  plannedToHikeOn: string | null = null
  difficultyOverride: string | null = null
  public: boolean = true
  submitForOfficialApproval: boolean = false
  makeOfficial: boolean = false // For trails@hiiker.co to directly set made_official_at
  generateAiDescription: boolean = false // For trails@hiiker.co to trigger AI description generation
  photosIds: string[] = []
  photosToUpload: File[] = []
  photoSignedIds: { [photoId: string]: string } = {}
  photoExistingIds: { [photoId: string]: number } = {}
  photosMarkedForRemoval: string[] = []
  existingHikeImages: Array<{
    id: number;
    clientId: string;
    previewUrl: string;
    isCover?: boolean;
    file?: {
      lgUrl?: string;
      mdUrl?: string;
      smUrl?: string;
      url?: string;
    };
  }> = []

  availableFeatureTags: string[] = []
  availableActivityTags: string[] = []
  selectedFeatureTags: string[] = []
  selectedActivityTags: string[] = []
  // User-created free-form tags: `available` is the user's previously-created
  // catalog; `selected` are the ones attached to the hike being saved. Unlike
  // feature/activity tags these are NOT filtered against the catalog, so a
  // freshly typed tag survives.
  availableCustomTags: string[] = []
  selectedCustomTags: string[] = []

  // Key-value route structure with per-route undo/redo
  routes: {
    [routeKey: string]: Route
  } = {}
  activeRouteKey: string | null = null

  // Stage-connector ids ("prevKey::nextKey") the user explicitly split via
  // "Disconnect stages" — these pairs never auto-connect again this session,
  // no matter how close their endpoints sit.
  disconnectedStageIds: string[] = []

  // Per-day stage destination: the POI/waypoint/highlight the day's end
  // marker is attached to. Only counts while the end marker actually sits on
  // the item (see services/stage-destinations.ts); stale entries are pruned
  // when stage decorations refresh.
  stageDestinations: { [dayNumber: number]: StageDestination } = {}

  // A day's stage-start (the first marker of that day's first main-line
  // route) attached to a map item, via "set as start point" on an empty
  // route — mirrors stageDestinations, but a single slot rather than a
  // day-keyed map: only one day's start is ever tracked as attached at a
  // time, whichever day it was most recently set on (usually day 1, but any
  // day with its own disconnected start can hold it). Only counts while that
  // first marker actually sits on the item; stale entries are pruned when
  // stage decorations refresh (see services/stage-destinations.ts).
  trailStartDestination: StageDestination | null = null

  // Frozen once when routeLocked is enabled (and re-derived on day-count
  // change / re-lock) — the single slicing source for every locked-mode
  // boundary drag. Never re-derived from current day routes, which may
  // already contain baked-in detours from a prior drag.
  lockedMasterTrail: { coordinates: number[][]; cumDist: number[] } | null = null

  // Per internal day boundary, keyed by the day whose END it marks (same
  // convention as stageDestinations). Always populated for every internal
  // boundary once locked; a drag repositions an entry, never deletes it.
  lockedBoundaries: { [dayNumber: number]: LockedBoundary } = {}

  // Legacy properties removed - use activeRoute instead

  isSnap: boolean = true
  center: number[] = [0, 0]
  zoom: number = 0
  miniCoordsBar: string = ''
  error?: string | null = undefined
  onMarkerMoveCalled = false;
  loading = false;
  clientId: string | null = null;
  shareableUrl: string | null = null;
  planMode: boolean = true;
  routePreference: string = "hike";
  hikeId: number | null = null;
  // Server id of the hike this plan was copied from ("Edit a Copy"). Sent as
  // originalHikeId on CreateHike so the backend propagates comments back to it.
  originalHikeId: number | null = null;
  trailDataId: number | null = null;
  tracerRouteGeoJson: any | null = null; // Stores GeoJSON for re-rendering on style change
  isHikeEdit: boolean = false;
  firstSymbolLayerId: string | null = null
  hoverPoint: { lng: number; lat: number; lineStringId: string; insertIndex: number } | null = null
  nearLine: boolean = false;
  _hoverMoveHandler: any = null;
  _hoverZoomHandler: any = null;
  _consolidatedHoverZoomHandler: any = null;
  mapHoverDistance: number | null = null;
  _hoverDistanceCache: { routeKey: string; lineStringCount: number; prefixLengths: number[] } | null = null;
  
  // Additional state properties
  routeCounter: number = 0;
  selectedMarker: MarkerFeature | null = null;
  selectedLineString: LineStringFeature | null = null;
  showModal: string = '';
  removalMode: boolean = false;
  
  // Day information for multi-day hikes, keyed by day number. `id` is the
  // persisted hike_day row and is only present when editing an existing hike
  // (the "edit a copy" path deliberately omits it so new rows are created) —
  // getHikeDaysAttributes sends it back as `id`, so it decides which row
  // receives a day's content. It therefore has to travel with the entry
  // whenever days are renumbered; see remapDayKeyedStateInPlace.
  dayInfo: {
    [dayNumber: number]: {
      id?: number;
      name: string;
      description: string;
    }
  } = {};

  // Guided/locked-route mode: when true, the plan is locked to a single
  // continuous master trail (derived from current main-line legs) and days
  // are re-split evenly via applyDayCount rather than freely edited. See
  // store/modules/actions/guided-route.ts.
  routeLocked: boolean = false;
  guidedDayCount: number | null = null;

  // Reference-trail overlay: draws the frozen reference trail (while locked)
  // as a dashed line, so a user can see what their days are sliced from.
  // Toggled via "Show reference trail" in the Actions panel's Tools section.
  // Never persisted/auto-restored — always starts off and only turns on via
  // an explicit click in the current session. See
  // store/modules/actions/master-trail-debug.ts.
  debugMasterTrailVisible: boolean = false;
  
  // Preview state for marker dragging. When `connector` is set the drag is a
  // stage-connector drag (moves the previous route's end AND the next
  // route's start together) and markerIndex/routeId are null.
  dragPreview: {
    isDragging: boolean;
    markerIndex: number | null;
    routeId: string | null;
    connector: { prevRouteKey: string; nextRouteKey: string } | null;
    originalPosition: [number, number] | null;
    previewPosition: [number, number] | null;
    snappedPosition: [number, number] | null;
    previewLineStrings: LineStringFeature[];
    pendingRequest: AbortController | null;
  } = {
    isDragging: false,
    markerIndex: null,
    routeId: null,
    connector: null,
    originalPosition: null,
    previewPosition: null,
    snappedPosition: null,
    previewLineStrings: [],
    pendingRequest: null,
  };

  // Track pending provisional line replacements to prevent duplicate undo states
  pendingProvisionalReplacements: Set<string> = new Set();

  // Whether the 30km route length warning has been shown this session
  routeLengthWarningShown: boolean = false;

  // Version counter for routesWithStats caching — incremented when route geometry changes
  _statsVersion: number = 0;

  // Command-pattern undo manager (session-only, not persisted to IndexedDB)
  undoManager: UndoManager = new UndoManager();

  // Area selection mode state
  isAreaSelecting: boolean = false;
  areaSelectedMarkers: Array<{ routeKey: string; markerIndex: number }> = [];
  
  // Hike highlights (custom POIs)
  hikeHighlights: Array<{
    clientId: string;
    type: string;
    title: string;
    description: string;
    coordinates: [number, number];
    dayNumber?: number;
    imageSignedIds?: Array<{
      signedId: string;
      clientId: string;
    }>;
    imagePreviewUrls?: string[];
    images?: Array<{
      id?: string | number;
      clientId: string;
      file?: {
        lgUrl?: string;
        smUrl?: string;
        mdUrl?: string;
        url?: string;
      };
      _destroy?: boolean;
    }>;
    // Audio (and future video/document) media attachments. New recordings/uploads
    // live in mediaSignedIds; media already persisted server-side live in `media`.
    mediaSignedIds?: Array<{
      signedId: string;
      clientId: string;
      mediaType: string;
      title?: string;
      previewUrl?: string;
    }>;
    media?: Array<{
      id?: string | number;
      clientId: string;
      mediaType: string;
      contentType?: string;
      title?: string;
      description?: string;
      url?: string;
      _destroy?: boolean;
    }>;
  }> = [];
}

declare global {
  interface Window {
    map: mapboxgl.Map;
  }
}

const state = new PlanTrailState()

const planTrailModule: Module<PlanTrailState, RootState> = {
  namespaced: true,
  state: state,
  actions: actions,
  mutations: mutations,
  getters: getters,
}

export default planTrailModule
